/**
 * Admin-only brand import: Name Suggestion Index (PH + world) and OSM wiki PH chains.
 * Runs in Wallet Admin (browser). Never called from user devices. Does not write
 * receipt merchants or private ledger data.
 */

import phChainsFile from "../../catalog/ph-chains.json";
import type { Institution, MerchantIcon, MerchantSourceKind, WalletCategory } from "./catalogTypes";
import {
  defaultGranularKind,
  encodeCountryScope,
  encodeMatchTerms,
  merchantSourceSlug,
  normalizeMerchantName,
  parseMatchTermsJSON,
} from "./catalogTypes";
import {
  classifyOsmTag,
  forcedMappingForName,
  FORCED_BRAND_MAPPINGS,
  MAPPED_OSM_TAGS,
  osmTagFromNsiPath,
} from "./merchantCategoryMap";
import { buildMergeQueue, type MerchantDraft, type MergePair } from "./merchantMerge";

/** Documented NSI dist path: https://github.com/osmlab/name-suggestion-index/wiki/Downloading-the-Data */
export const NSI_CDN_VERSION = "latest";
export const NSI_JSON_URL = `https://cdn.jsdelivr.net/npm/name-suggestion-index@${NSI_CDN_VERSION}/dist/json/nsi.min.json`;
export const OSM_PH_CHAINS_WIKI = "https://wiki.openstreetmap.org/wiki/Philippines/Chains";

type PhChainSeed = {
  displayName?: string;
  aliases?: string[];
  osm?: string;
  wikidataId?: string;
};

type PhChainsFile = {
  source?: string;
  refreshed?: string;
  refresh?: string;
  chains?: PhChainSeed[];
};

const phChains = phChainsFile as PhChainsFile;

export const PH_CHAINS_REFRESHED = phChains.refreshed ?? "";
export const PH_CHAINS_REFRESH_NOTE = phChains.refresh ?? "";
export const PH_CHAINS_SOURCE = phChains.source ?? OSM_PH_CHAINS_WIKI;

type NsiItem = {
  displayName?: string;
  id?: string;
  locationSet?: { include?: unknown; exclude?: unknown };
  matchNames?: string[];
  tags?: Record<string, string>;
  fromTemplate?: boolean;
};

type NsiFile = {
  nsi?: Record<string, { properties?: { path?: string }; items?: NsiItem[] }>;
};

export type BrandImportOptions = {
  includeNsi: boolean;
  includePhChains: boolean;
  includeInstitutions: boolean;
};

export type BrandImportResult = {
  incoming: number;
  nsiKept: number;
  nsiSkippedWorld: number;
  phChains: number;
  institutions: number;
  newDrafts: MerchantDraft[];
  exact: MergePair[];
  similar: MergePair[];
};

function includeCodes(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((value) => String(value).trim().toLowerCase())
    .filter((value) => /^[a-z]{2}$/.test(value) || value === "001" || value.includes("."));
}

function locationKeep(include: string[]): { keep: boolean; isPH: boolean; isWorld: boolean } {
  const isPH = include.includes("ph");
  const isWorld = include.includes("001");
  return { keep: isPH || isWorld, isPH, isWorld };
}

function wikidataFromTags(tags: Record<string, string> | undefined): string | null {
  if (!tags) return null;
  const id =
    tags["brand:wikidata"] ||
    tags["operator:wikidata"] ||
    tags["network:wikidata"] ||
    tags.wikidata ||
    "";
  const trimmed = id.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^Q(\d+)$/i);
  return match ? `Q${match[1]}` : trimmed;
}

function isDissolvedOrIncomplete(item: NsiItem): boolean {
  if (item.fromTemplate && !wikidataFromTags(item.tags)) return true;
  const tags = item.tags ?? {};
  if (tags.end_date || tags["end_date"]) return true;
  if ((tags.opening_hours ?? "").toLowerCase() === "closed") return true;
  if (!(item.displayName ?? tags.name ?? tags.brand ?? "").trim()) return true;
  return false;
}

function draftBase(input: {
  displayName: string;
  aliases?: string[];
  osm?: string | null;
  wikidataId?: string | null;
  nsiId?: string | null;
  sourceKind: MerchantSourceKind;
  sourceName: string;
  countries: string[];
  sortOrder: number;
}): MerchantDraft {
  const forced = forcedMappingForName(input.displayName, input.aliases ?? []);
  const classified = classifyOsmTag(input.osm);
  const walletCategory: WalletCategory = forced?.walletCategory ?? classified.walletCategory;
  const granularKind = forced?.granularKind ?? classified.granularKind ?? defaultGranularKind(walletCategory);
  const displayName = forced?.displayName || input.displayName.trim();
  const normalizedName = normalizeMerchantName(displayName);
  const slugSource = input.nsiId || normalizedName;
  const sourceSlug = merchantSourceSlug(slugSource) || merchantSourceSlug(displayName);
  const aliases = [
    ...(input.aliases ?? []),
    ...(forced?.aliases ?? []),
    input.nsiId ?? "",
  ]
    .map((alias) => alias.trim().toLowerCase())
    .filter((alias) => alias && alias !== normalizedName);

  return {
    displayName,
    normalizedName,
    matchTerms: aliases,
    matchTermsJSON: encodeMatchTerms(aliases),
    sourceSlug,
    sourceName: input.sourceName,
    brandHex: null,
    iconURL: null,
    categoryHint: null,
    walletCategory,
    granularKind,
    countryScopeJSON: encodeCountryScope(input.countries.length ? input.countries : ["001"]),
    wikidataId: input.wikidataId ?? null,
    nsiId: input.nsiId ?? null,
    sourceKind: input.sourceKind,
    mergedIntoRecordName: null,
    isActive: true,
    sortOrder: input.sortOrder,
  };
}

function nsiItemToDraft(path: string, item: NsiItem, sortOrder: number): MerchantDraft | null {
  if (isDissolvedOrIncomplete(item)) return null;
  const include = includeCodes(item.locationSet?.include);
  const loc = locationKeep(include);
  const osm = osmTagFromNsiPath(path);
  const displayName = (item.displayName || item.tags?.name || item.tags?.brand || "").trim();
  if (!displayName) return null;

  const forced = forcedMappingForName(displayName, item.matchNames ?? []);
  const worldWithoutMappedTag = loc.isWorld && !loc.isPH && osm && !MAPPED_OSM_TAGS.has(osm);
  if (!loc.keep && !forced) return null;
  if (worldWithoutMappedTag && !forced && !loc.isPH) return null;

  const countries = include.filter((code) => /^[a-z]{2}$/.test(code) || code === "001");
  return draftBase({
    displayName,
    aliases: item.matchNames ?? [],
    osm,
    wikidataId: wikidataFromTags(item.tags),
    nsiId: item.id?.trim() || null,
    sourceKind: "nsi",
    sourceName: "nsi",
    countries: countries.length ? countries : loc.isPH ? ["ph"] : ["001"],
    sortOrder,
  });
}

export async function fetchNsiBrandDrafts(
  onProgress?: (message: string) => void,
): Promise<{ drafts: MerchantDraft[]; skippedWorld: number }> {
  onProgress?.("Downloading Name Suggestion Index…");
  const res = await fetch(NSI_JSON_URL);
  if (!res.ok) {
    throw new Error(`Could not load NSI (${res.status}). Check ${NSI_JSON_URL}`);
  }
  const payload = (await res.json()) as NsiFile;
  const trees = payload.nsi ?? {};
  const drafts: MerchantDraft[] = [];
  let skippedWorld = 0;
  let sortOrder = 8000;

  for (const [path, group] of Object.entries(trees)) {
    const tree = path.split("/")[0];
    if (tree !== "brands" && tree !== "operators" && tree !== "transit") continue;
    for (const item of group.items ?? []) {
      const include = includeCodes(item.locationSet?.include);
      const loc = locationKeep(include);
      const osm = osmTagFromNsiPath(path);
      const forced = forcedMappingForName(item.displayName, item.matchNames ?? []);
      const worldWithoutMappedTag = loc.isWorld && !loc.isPH && osm && !MAPPED_OSM_TAGS.has(osm);
      if (worldWithoutMappedTag && !forced && !loc.isPH) {
        skippedWorld += 1;
        continue;
      }
      const draft = nsiItemToDraft(group.properties?.path ?? path, item, sortOrder);
      if (!draft) continue;
      drafts.push(draft);
      sortOrder += 10;
    }
  }

  onProgress?.(`Filtered NSI to ${drafts.length.toLocaleString()} PH + mapped global brands.`);
  return { drafts, skippedWorld };
}

export function phChainDrafts(): MerchantDraft[] {
  return (phChains.chains ?? [])
    .map((chain, index) => {
      const displayName = (chain.displayName ?? "").trim();
      if (!displayName) return null;
      return draftBase({
        displayName,
        aliases: chain.aliases ?? [],
        osm: chain.osm,
        wikidataId: chain.wikidataId ?? null,
        nsiId: null,
        sourceKind: "osm-wiki",
        sourceName: "osm-wiki",
        countries: ["ph"],
        sortOrder: 4000 + index * 10,
      });
    })
    .filter((row): row is MerchantDraft => Boolean(row));
}

export function institutionBrandDrafts(institutions: readonly Institution[]): MerchantDraft[] {
  return institutions
    .filter((row) => row.isActive !== false)
    .map((row, index) => {
      const forced = forcedMappingForName(row.name, [row.slug]);
      let granularKind = "amenity/bank";
      if (forced) {
        granularKind = forced.granularKind;
      } else if (row.kind === "transportation") {
        granularKind = "cms/transit";
      } else if (row.kind === "insurer") {
        granularKind = "office/insurance";
      } else if (row.kind === "other") {
        granularKind = defaultGranularKind("other");
      }
      return draftBase({
        displayName: forced?.displayName || row.name,
        aliases: [row.slug],
        osm: granularKind,
        sourceKind: "institution",
        sourceName: "institution",
        countries: [(row.countryCode || "PH").toLowerCase()],
        sortOrder: 3500 + index * 10,
      });
    });
}

function existingToDraft(row: MerchantIcon): MerchantDraft {
  return {
    ...row,
    matchTerms: parseMatchTermsJSON(row.matchTermsJSON),
  };
}

/**
 * Dedupes incoming drafts that share nsiId / wikidata / normalizedName before
 * the review queue. The first hit (NSI preferred) keeps the row; later hits
 * union aliases and fill empty wikidata / nsiId / icon fields.
 */
function collapseIdenticalIncoming(drafts: MerchantDraft[]): MerchantDraft[] {
  const rank: Record<MerchantSourceKind, number> = {
    "simple-icons": 0,
    nsi: 1,
    "osm-wiki": 2,
    institution: 3,
    cms: 4,
  };
  const sorted = [...drafts].sort(
    (a, b) => (rank[a.sourceKind] ?? 9) - (rank[b.sourceKind] ?? 9),
  );
  const out: MerchantDraft[] = [];
  const byNsi = new Map<string, MerchantDraft>();
  const byWiki = new Map<string, MerchantDraft>();
  const byName = new Map<string, MerchantDraft>();

  for (const draft of sorted) {
    const hit =
      (draft.nsiId ? byNsi.get(draft.nsiId) : undefined) ??
      (draft.wikidataId ? byWiki.get(draft.wikidataId) : undefined) ??
      byName.get(draft.normalizedName);
    if (hit) {
      const terms = Array.from(
        new Set([
          ...(hit.matchTerms ?? []),
          ...parseMatchTermsJSON(hit.matchTermsJSON),
          ...(draft.matchTerms ?? []),
          ...parseMatchTermsJSON(draft.matchTermsJSON),
          draft.displayName.toLowerCase(),
        ]),
      );
      hit.matchTerms = terms;
      hit.matchTermsJSON = encodeMatchTerms(terms);
      hit.wikidataId = hit.wikidataId || draft.wikidataId;
      hit.nsiId = hit.nsiId || draft.nsiId;
      hit.iconURL = hit.iconURL || draft.iconURL;
      hit.brandHex = hit.brandHex || draft.brandHex;
      continue;
    }
    out.push(draft);
    if (draft.nsiId) byNsi.set(draft.nsiId, draft);
    if (draft.wikidataId) byWiki.set(draft.wikidataId, draft);
    byName.set(draft.normalizedName, draft);
  }
  return out;
}

export async function previewBrandImport(
  existing: readonly MerchantIcon[],
  options: BrandImportOptions,
  extra?: { institutions?: Institution[]; onProgress?: (message: string) => void },
): Promise<BrandImportResult> {
  const incoming: MerchantDraft[] = [];
  let nsiKept = 0;
  let nsiSkippedWorld = 0;
  let phCount = 0;
  let instCount = 0;

  if (options.includeNsi) {
    const nsi = await fetchNsiBrandDrafts(extra?.onProgress);
    incoming.push(...nsi.drafts);
    nsiKept = nsi.drafts.length;
    nsiSkippedWorld = nsi.skippedWorld;
  }
  if (options.includePhChains) {
    const chains = phChainDrafts();
    incoming.push(...chains);
    phCount = chains.length;
  }
  if (options.includeInstitutions && extra?.institutions) {
    const inst = institutionBrandDrafts(extra.institutions);
    incoming.push(...inst);
    instCount = inst.length;
  }

  for (const forced of FORCED_BRAND_MAPPINGS) {
    if (incoming.some((row) => row.normalizedName === forced.normalizedName)) continue;
    incoming.push(
      draftBase({
        displayName: forced.displayName || forced.normalizedName,
        aliases: forced.aliases,
        osm: forced.granularKind,
        sourceKind: "cms",
        sourceName: "cms",
        countries: ["ph"],
        sortOrder: 3000,
      }),
    );
  }

  const collapsed = collapseIdenticalIncoming(incoming);
  extra?.onProgress?.("Matching against existing merchant brands…");
  const queue = buildMergeQueue(existing.map(existingToDraft), collapsed);
  return {
    incoming: collapsed.length,
    nsiKept,
    nsiSkippedWorld,
    phChains: phCount,
    institutions: instCount,
    ...queue,
  };
}

export function nextMerchantSortOrder(rows: readonly MerchantIcon[]): number {
  const max = rows.reduce((acc, row) => Math.max(acc, row.sortOrder ?? 0), 0);
  return max + 10;
}
